import { ReportHeader } from "views/applications-id-report/components/ReportHeader";
import { Section } from "views/applications-id-report/components/Section";
import { has, toMoney } from "shared/utils";
import { DateTime } from "luxon";
import { Hide, Show } from "shared/components";
import {
  CombinedIncomeReport,
  EditIncomeModalData,
  EditTransactionsDrawerData,
  OverrideIncomeModalData
} from "./types";
import { NetIncomeChart } from "./slices/NetIncomeGraphSlice";
import {
  ApplicationStatus,
  ExecutionModes,
  VerificationNames,
  VerificationStatus
} from "shared/types";
import { DefinedQueryObserverResult, useQuery } from "@tanstack/react-query";
import { VerificationReport } from "types/landlord/reports";
import { getVerificationQueryKeys } from "views/applications-id-report/utils/getVerificationQueryKeys";
import api from "api/landlord/applications";
import { Loader } from "shared/components/Loader";
import { ReportException } from "views/applications-id-report/components/ReportException";
import { Applicant, Application } from "types/application";
import { mapNetIncomeAnalysis } from "./helpers";
import { ExpandableTable } from "./components/UniversalTable";
import { activityLogsQueryKey } from "shared/queries/useActivityLogsController";
import { AdjustedApplicationRentActivity } from "shared/app/ActivityLog/types";
import { instance } from "api/apiBase";
import queryString from "qs";
import { AdjustedApplicationRentActivityLog } from "views/applications-id-report/components/AdjustedApplicationRentIncomeSection";
import { IncomeSlice } from "./slices/TablesSlice/IncomeSlice";
import { DocumentWarnings } from "./slices/DocumentWarningsSlice";
import { PrintReady } from "views/applications-id-report/providers/PrintProvider";
import { getAdditionalDataPerVerificationType } from "./utils";
import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { EditIncomeModal } from "./components/EditIncomeModal";
import { ReverifyIncomeModal } from "./components/ReverifyIncomeModal";
import { ReverifyFlows } from "./components/ReverifyIncomeModal/types";
import { FEATURES } from "types/landlord/partner";
import { useAuthContext } from "providers/Auth";
import { ReverifyRequest } from "./types/reverify";
import { NoIncomeSection } from "./components/NoIncomeSection";
import { NetIncomeAnalysisExpandedRow } from "./components/NetIncomeAnalysisExpandedRow";
import { Verification } from "shared/types";
import { PendingReportView } from "views/applications-id-report/components/ReportsFallback/PendingReportView";
import { UnavailableReportView } from "views/applications-id-report/components/ReportsFallback/UnavailableReportView";
import IncomeSummary from "./components/IncomeSummary";
import { VerificationOverrideModal } from "./components/VerificationOverrideModal";
import { SkippedReportView } from "views/applications-id-report/components/ReportsFallback/SkippedReportView";
import { DebtDetails } from "./components/DebtDetails";
import {
  INCOMPLETE_APPLICATION_STATUSES,
  INCOMPLETE_VERIFICATION_STATUSES
} from "./constants";
import { EditTransactionsDrawer } from "./components/EditTransactionsDrawer";

export const CombinedIncomeReportSlice = ({
  verificationId,
  applicantId,
  applicationId,
  hasCreditReport,
  applicant,
  applicationStatus,
  currentVerification,
  applicationMeta
}: {
  verificationId?: number;
  applicantId?: number;
  applicationId?: number;
  hasCreditReport?: boolean;
  applicant: Applicant;
  applicationStatus: ApplicationStatus;
  currentVerification: Verification;
  applicationMeta: DefinedQueryObserverResult<Application | undefined, unknown>;
}) => {
  const { partner } = useAuthContext();
  const isSelfServeReverifyEnabled =
    partner?.features.includes(FEATURES.WIP_FEAT_INCOME_SELF_SERVE_REVERIFY) ??
    false;

  const [showReverifyIncomeModal, setShowReverifyIncomeModal] = useState<
    number | false
  >(false);
  const [showEditIncomeModal, setShowEditIncomeModal] = useState<
    false | EditIncomeModalData
  >(false);
  const [showOverrideVerificationModal, setShowOverrideVerificationModal] =
    useState<false | OverrideIncomeModalData>(false);
  const [showEditTransactionsDrawer, setShowEditTransactionsDrawer] = useState<
    false | EditTransactionsDrawerData
  >(false);
  const [isArchivedVerificationSelected, setIsArchivedVerificationSelected] =
    useState(false);

  const {
    data: reportData,
    isError,
    isLoading,
    refetch
  } = useQuery<VerificationReport<CombinedIncomeReport>>({
    queryKey: getVerificationQueryKeys({
      reportName: VerificationNames.PetScreeningReport,
      applicationId: Number(applicationId || 0),
      applicantId: Number(applicantId || 0),
      verificationId
    }),
    queryFn: () =>
      api
        .getApplicantVerification(
          Number(applicationId || 0),
          Number(applicantId || 0),
          verificationId || 0
        )
        .then((data) => data.verification)
  });

  const applicationRentActivityLogsMeta = useQuery<
    AdjustedApplicationRentActivity[]
  >({
    queryKey: activityLogsQueryKey(applicant.application_id),
    queryFn: () => {
      const params = {
        events: [
          "application_rent_adjusted",
          "application_rent_automatically_adjusted"
        ]
      };
      const url =
        `portal/v1/applications/${applicant.application_id}/activity_logs?` +
        queryString.stringify(params, { arrayFormat: "brackets" });
      return instance.get(url).then(({ data }) => data.activity_logs);
    },
    enabled: true
  });

  const data = reportData?.report;

  // Because status does not yet exist on data.bank_statements
  const filteredBankStatements = useMemo(() => {
    if (!data?.bank_statements) return {};

    const resetBankStatementIds = new Set(
      reportData?.income_sources
        ?.filter((incomeSource) => incomeSource.status === "reset")
        .map((incomeSource) => incomeSource!.id)
    );

    return Object.fromEntries(
      Object.entries(data.bank_statements).filter(
        ([id]) => !resetBankStatementIds.has(+id)
      )
    );
  }, [reportData?.income_sources]);

  if (isLoading) return <Loader />;
  if (isError)
    return (
      <Section maxWidth="none" sectionTitle="Income summary">
        <ReportException
          title="Failed to load income report"
          applicationStatus={applicationStatus}
          applicationId={applicationId || "0"}
        />
      </Section>
    );

  if (
    !reportData ||
    !data ||
    !Object.keys(data).length ||
    !applicationMeta.data
  )
    return null;

  const settings = reportData?.settings;
  const combinedIncomeStep = reportData?.steps?.find(
    (step) => step.name === "combined_income"
  );
  const reverifyRequests =
    (combinedIncomeStep?.data as { reverify_requests?: ReverifyRequest[] })
      ?.reverify_requests ?? [];

  if (
    currentVerification?.status === VerificationStatus.Skipped &&
    currentVerification.settings?.execution_mode ===
      ExecutionModes.Conditionally
  )
    return (
      <SkippedReportView
        refetch={applicationMeta.refetch}
        applicantId={applicant.id}
        verificationId={currentVerification.id}
        applicationId={applicationMeta.data.id}
        verificationStatus={currentVerification.status}
      />
    );

  if (currentVerification?.status === VerificationStatus.Canceled)
    return <PendingReportView message="Report was canceled" />;

  if (currentVerification?.status === VerificationStatus.NoDataAvailable)
    return <UnavailableReportView />;

  const noIncome = data.incomes.find((income) => income.type === "no_income");
  if (noIncome) {
    const housingAssistance = data.incomes.find(
      (income) => income.type === "housing_assistance"
    );

    return (
      <NoIncomeSection
        noIncome={noIncome}
        housingAssistance={housingAssistance}
        applicant={applicant}
        verificationId={verificationId}
      />
    );
  }

  const summary = data.total_summary,
    netIncomeData = data.net_income_analysis,
    bankStatements = data.bank_statements,
    incomes = data.incomes;

  const verificationStatus = reportData?.status ?? VerificationStatus.Pending;
  const isVerificationIncomplete =
    INCOMPLETE_VERIFICATION_STATUSES.includes(verificationStatus) &&
    INCOMPLETE_APPLICATION_STATUSES.includes(applicationMeta.data.status);

  return (
    <div>
      <ReportHeader
        applicationStatus={applicationStatus}
        applicationId={applicationId || "0"}
        title="Income history"
        applicantId={applicantId || 0}
        name={VerificationNames.CombinedIncome}
        reportDate={
          isVerificationIncomplete ? undefined : currentVerification.updated_at
        }
        hidePrint={isVerificationIncomplete}
      />

      <Show if={verificationId}>
        <PrintReady verificationId={verificationId!} />
      </Show>

      <Section
        sectionTitle="Income summary"
        maxWidth="100%"
        sectionClassName="p-0"
      >
        <IncomeSummary
          isSelfServeReverifyEnabled={isSelfServeReverifyEnabled}
          incomes={incomes}
          reverifyRequests={reverifyRequests}
          summary={summary}
          combinedIncomeReport={data}
          hasCreditReport={Boolean(hasCreditReport)}
          verificationStatus={verificationStatus}
          isSummaryPending={isVerificationIncomplete}
        />
      </Section>

      <DocumentWarnings incomes={incomes} />

      <Hide if={isVerificationIncomplete}>
        <Section sectionTitle="Net income analysis" maxWidth="1100px">
          <NetIncomeChart data={netIncomeData} />

          <div className="mt-[60px]">
            <ExpandableTable
              hideHeader
              rowClassName="items-center pr-4"
              headerRowClassName="text-content-1000 text-sm font-medium"
              columns={[
                {
                  header: "month",
                  accessor: (row) =>
                    DateTime.fromISO(row.month).toFormat("MMMM, yyyy"),
                  widthClass: "w-full"
                },
                {
                  header: "total",
                  accessor: (row) => toMoney(row.total),
                  widthClass: "w-[120px] shrink-0"
                }
              ]}
              rows={mapNetIncomeAnalysis(netIncomeData)}
              renderExpanded={(original) => (
                <NetIncomeAnalysisExpandedRow original={original} />
              )}
            />
          </div>
        </Section>
      </Hide>

      {incomes.map((income, index) => {
        return (
          <IncomeSlice
            key={index}
            income={income}
            applicationId={applicationId}
            applicationStatus={applicationStatus}
            settings={settings}
            additionalData={getAdditionalDataPerVerificationType(
              data,
              income,
              filteredBankStatements
            )}
            setShowEditIncomeModal={setShowEditIncomeModal}
            setShowEditTransactionsDrawer={setShowEditTransactionsDrawer}
            setShowReverifyIncomeModal={setShowReverifyIncomeModal}
            setShowOverrideVerificationModal={setShowOverrideVerificationModal}
            isManualApplicant={applicant.is_manual}
            setIsArchivedVerificationSelected={
              setIsArchivedVerificationSelected
            }
            verificationId={verificationId}
            applicantId={applicantId}
            reverifyRequests={reverifyRequests}
            verificationStatus={reportData?.status}
            onReverifyRequestCancelled={refetch}
            applicant={applicant}
            isVerificationIncomplete={isVerificationIncomplete}
          />
        );
      })}

      <Show
        if={
          (!isSelfServeReverifyEnabled || !isArchivedVerificationSelected) &&
          Boolean(
            summary.debt_gross_income_ratio ||
            summary.debt_net_income_ratio ||
            summary.debt_self_attested_gross_income_ratio
          )
        }
      >
        <DebtDetails
          summary={summary}
          isSelfServeReverifyEnabled={isSelfServeReverifyEnabled}
          isVerificationIncomplete={isVerificationIncomplete}
        />
      </Show>

      <div className="space-y-8 p-6 pt-0 text-sm">
        {applicationRentActivityLogsMeta.data?.map((activityItem, index) => (
          <AdjustedApplicationRentActivityLog
            key={index}
            activityItem={activityItem}
          />
        ))}
      </div>

      <EditTransactionsDrawer
        onSuccess={refetch}
        applicationId={Number(applicationId || 0)}
        applicant={applicant}
        verificationId={verificationId || 0}
        opened={Boolean(showEditTransactionsDrawer)}
        onClose={() => setShowEditTransactionsDrawer(false)}
        allBankStatements={bankStatements}
        {...(typeof showEditTransactionsDrawer === "object"
          ? showEditTransactionsDrawer
          : {})}
      />

      <Show if={Boolean(showEditIncomeModal)}>
        <EditIncomeModal
          onSuccess={refetch}
          applicationId={Number(applicationId || 0)}
          applicant={applicant}
          verificationId={verificationId || 0}
          opened={Boolean(showEditIncomeModal)}
          onClose={() => setShowEditIncomeModal(false)}
          {...(typeof showEditIncomeModal === "object"
            ? showEditIncomeModal
            : {})}
        />
      </Show>

      <Show if={has(showOverrideVerificationModal)}>
        <VerificationOverrideModal
          onSuccess={() => {
            toast.success("Successfully overrode verification");
            refetch();
            setShowOverrideVerificationModal(false);
          }}
          applicationId={Number(applicationId || 0)}
          applicant={applicant}
          verificationId={verificationId || 0}
          opened={Boolean(showOverrideVerificationModal)}
          onClose={() => setShowOverrideVerificationModal(false)}
          {...(typeof showOverrideVerificationModal === "object"
            ? showOverrideVerificationModal
            : {})}
        />
      </Show>

      <Show if={has(showReverifyIncomeModal)}>
        <ReverifyIncomeModal
          onSuccess={() => {
            toast.success("Re-verification requested successfully");
            refetch();
            setShowReverifyIncomeModal(false);
          }}
          settings={settings}
          applicant={applicant}
          applicationId={applicationId || 0}
          verificationId={verificationId || 0}
          opened
          onClose={() => {
            setShowReverifyIncomeModal(false);
          }}
          income={incomes.find((i) => i.id === showReverifyIncomeModal)}
          initialFlow={
            isSelfServeReverifyEnabled
              ? ReverifyFlows.SelectOption
              : ReverifyFlows.ReVerifyHere
          }
        />
      </Show>
    </div>
  );
};
